import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase/client';
import { TransbankService } from '../services/transbank.service';

interface AnticipData {
  anticipo_monto: number;
  anticipo_deadline: string;
  valor_final: number;
  estado_pago_consulta: string;
  fecha_hora: string;
}

const LOGO = 'https://conectemoschile.cl/assets/img/logo.png';

function formatDeadline(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Santiago',
  });
}

function formatPrice(n: number): string {
  return n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
}

const PaymentPage: React.FC = () => {
  const { consultaId } = useParams<{ consultaId: string }>();
  const navigate        = useNavigate();

  const [data,      setData]      = useState<AnticipData | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [paying,    setPaying]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [webpayData, setWebpayData] = useState<{ url: string; token: string } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!consultaId) { setError('Enlace inválido.'); setLoading(false); return; }

    supabase
      .rpc('get_consulta_payment_info', { p_consulta_id: consultaId })
      .then(({ data: rows, error: err }) => {
        const row = rows?.[0];
        if (err || !row) { setError('Reserva no encontrada.'); }
        else              { setData(row as AnticipData); }
        setLoading(false);
      });
  }, [consultaId]);

  useEffect(() => {
    if (webpayData && formRef.current) {
      formRef.current.submit();
    }
  }, [webpayData]);

  const handlePagar = async () => {
    if (!consultaId) return;
    setPaying(true);
    setError(null);
    try {
      const result = await TransbankService.initAnticipo(consultaId);
      if (!result?.url || !result?.token) {
        throw new Error('Respuesta inválida de Transbank.');
      }
      setWebpayData({ url: result.url, token: result.token });
    } catch (err: any) {
      setError(err.message ?? 'Error al iniciar el pago.');
      setPaying(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────
  if (loading) return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <p style={{ color: '#718096', textAlign: 'center' }}>Cargando datos de tu reserva…</p>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────────
  if (error || !data) return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <p style={{ color: '#DC2626', textAlign: 'center' }}>{error ?? 'Error inesperado.'}</p>
        <button onClick={() => navigate('/')} style={btnSecondaryStyle}>Volver al inicio</button>
      </div>
    </div>
  );

  // ── Ya pagó anticipo ─────────────────────────────────────────────
  if (data.estado_pago_consulta === 'anticipo_pagado' || data.estado_pago_consulta === 'pago_completo') return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 16 }}>✅</div>
        <h2 style={{ ...headingStyle, color: '#15803D' }}>Anticipo ya registrado</h2>
        <p style={bodyStyle}>Tu reserva está confirmada. No es necesario realizar otro pago.</p>
        <button onClick={() => navigate('/')} style={btnPrimaryStyle}>Volver al inicio</button>
      </div>
    </div>
  );

  // ── Formulario de pago ───────────────────────────────────────────
  const pastDeadline = data.anticipo_deadline && new Date(data.anticipo_deadline) < new Date();

  // Form oculto Webpay — montado en DOM real para que submit() funcione
  const webpayForm = webpayData ? (
    <form ref={formRef} method="POST" action={webpayData.url} style={{ display: 'none' }}>
      <input type="hidden" name="token_ws" value={webpayData.token} />
    </form>
  ) : null;

  return (
    <div style={pageStyle}>
      {webpayForm}
      <div style={cardStyle}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src={LOGO} alt="Conectemos Chile" width={52} style={{ borderRadius: 10, marginBottom: 12 }} />
          <p style={{ margin: 0, color: '#B8833A', fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>
            Conectemos Chile
          </p>
          <h1 style={{ margin: '8px 0 0', fontSize: 22, fontWeight: 700, color: '#1A2236' }}>
            Pago de anticipo
          </h1>
        </div>

        {/* Monto */}
        <div style={{
          background: '#EEF4FF', borderRadius: 12, padding: '20px 24px', marginBottom: 20,
        }}>
          <p style={{ margin: '0 0 4px', color: '#1E4A8F', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>
            Monto a pagar
          </p>
          <p style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 700, color: '#1A2236' }}>
            {formatPrice(data.anticipo_monto)}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#718096' }}>
            50% del valor total ({formatPrice(data.valor_final)})
          </p>
        </div>

        {/* Deadline */}
        {data.anticipo_deadline && (
          <div style={{
            background: pastDeadline ? '#FEF2F2' : '#FFFBEB',
            border: `1px solid ${pastDeadline ? '#FCA5A5' : '#FCD34D'}`,
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 24,
          }}>
            <p style={{ margin: 0, fontSize: 13, color: pastDeadline ? '#DC2626' : '#92400E' }}>
              {pastDeadline
                ? '⚠️ El plazo de pago venció, pero aún podés confirmar tu reserva.'
                : `⏰ Plazo para pagar: ${formatDeadline(data.anticipo_deadline)}`
              }
            </p>
          </div>
        )}

        {/* Error inline */}
        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            <p style={{ margin: 0, color: '#DC2626', fontSize: 13 }}>{error}</p>
          </div>
        )}

        {/* Botón pagar */}
        <button
          onClick={handlePagar}
          disabled={paying}
          style={{ ...btnPrimaryStyle, width: '100%', marginBottom: 12, opacity: paying ? 0.7 : 1 }}
        >
          {paying ? 'Iniciando pago…' : '💳 Pagar con Webpay'}
        </button>

        <p style={{ margin: 0, fontSize: 12, color: '#94A3B8', textAlign: 'center' }}>
          Serás redirigido al formulario seguro de Transbank
        </p>

        <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #E2E8F0' }} />

        <p style={{ margin: 0, fontSize: 12, color: '#94A3B8', textAlign: 'center' }}>
          ¿Problemas? Llamanos al{' '}
          <a href="tel:+56978009119" style={{ color: '#1E4A8F' }}>+56 9 7800 9119</a>
        </p>
      </div>
    </div>
  );
};

// ── Estilos inline (tokens del sistema web público) ───────────────
const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#F7F9FC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 16px',
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  padding: '40px 36px',
  maxWidth: 440,
  width: '100%',
};

const headingStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 22,
  fontWeight: 700,
  textAlign: 'center',
};

const bodyStyle: React.CSSProperties = {
  margin: '0 0 24px',
  fontSize: 15,
  color: '#4A5568',
  lineHeight: 1.7,
  textAlign: 'center',
};

const btnPrimaryStyle: React.CSSProperties = {
  background: '#1E4A8F',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '14px 24px',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'block',
  textAlign: 'center',
};

const btnSecondaryStyle: React.CSSProperties = {
  ...btnPrimaryStyle,
  background: '#EEF4FF',
  color: '#1E4A8F',
  marginTop: 16,
};

export default PaymentPage;
